import {
  BoxGeometry,
  BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  Float32BufferAttribute,
  IcosahedronGeometry,
  TorusGeometry,
} from 'three';

/**
 * Low-poly models of extraction sites (#103, the author's request of
 * 21.10.2026): a derrick on a gas giant, a mine on asteroids and rocky
 * worlds. Each is one geometry, so a pool of them is one draw call.
 */

/** Merges parts into one non-indexed geometry: position and normal only, for flat shading. */
function merge(parts: BufferGeometry[]): BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  for (const part of parts) {
    const flat = part.index ? part.toNonIndexed() : part;
    positions.push(...(flat.getAttribute('position').array as Float32Array));
    normals.push(...(flat.getAttribute('normal').array as Float32Array));
    part.dispose();
    if (flat !== part) flat.dispose();
  }
  const out = new BufferGeometry();
  out.setAttribute('position', new Float32BufferAttribute(positions, 3));
  out.setAttribute('normal', new Float32BufferAttribute(normals, 3));
  return out;
}

/** A gas derrick: a platform, a four-sided lattice tower, a crown block and a tank. Base at y = 0. */
export function derrickGeometry(): BufferGeometry {
  return merge([
    new CylinderGeometry(0.22, 0.26, 0.08, 6).translate(0, 0.04, 0),
    new CylinderGeometry(0.035, 0.14, 0.6, 4).translate(0, 0.38, 0),
    new BoxGeometry(0.12, 0.07, 0.12).translate(0, 0.7, 0),
    new CylinderGeometry(0.075, 0.075, 0.18, 6).translate(0.17, 0.17, 0.05),
    new CylinderGeometry(0.012, 0.012, 0.3, 3).rotateZ(Math.PI / 2.6).translate(0.07, 0.36, 0.05),
  ]);
}

/** A mine: a low shed, a headframe with its wheel, and a drill bit into the rock. Base at y = 0. */
export function mineGeometry(): BufferGeometry {
  return merge([
    new BoxGeometry(0.34, 0.12, 0.26).translate(0, 0.06, 0),
    new BoxGeometry(0.06, 0.36, 0.06).translate(-0.1, 0.3, 0),
    new BoxGeometry(0.06, 0.3, 0.06).translate(0.02, 0.27, 0).rotateZ(-0.35),
    new TorusGeometry(0.08, 0.02, 3, 8).translate(-0.06, 0.46, 0),
    new ConeGeometry(0.07, 0.2, 4).rotateX(Math.PI).translate(0.1, -0.08, 0),
  ]);
}

/** One rock of an asteroid found in a cloud (they are not part of the static map). */
export function rockGeometry(): BufferGeometry {
  return new IcosahedronGeometry(1, 0);
}

/*
 * Ships (#111, the author's request of 22.10.2026: «все корабли — вытянутые
 * пирамиды»). A fleet is drawn as its biggest ship; each class has its own
 * silhouette, nose along +z, about a hex across at most.
 */
const nose = (g: BufferGeometry) => g.rotateX(Math.PI / 2);

export function corvetteGeometry(): BufferGeometry {
  return merge([
    nose(new ConeGeometry(0.17, 0.78, 3)),
    new BoxGeometry(0.62, 0.04, 0.16).translate(0, 0, -0.18),
  ]);
}

export function frigateGeometry(): BufferGeometry {
  return merge([
    nose(new ConeGeometry(0.26, 0.86, 4)),
    nose(new CylinderGeometry(0.07, 0.07, 0.5, 5)).translate(0.26, 0, -0.1),
    nose(new CylinderGeometry(0.07, 0.07, 0.5, 5)).translate(-0.26, 0, -0.1),
  ]);
}

export function cruiserGeometry(): BufferGeometry {
  return merge([
    nose(new CylinderGeometry(0.2, 0.26, 0.95, 6)).translate(0, 0, -0.08),
    nose(new ConeGeometry(0.2, 0.36, 6)).translate(0, 0, 0.56),
    new BoxGeometry(0.2, 0.16, 0.3).translate(0, 0.22, -0.3),
    new BoxGeometry(0.7, 0.05, 0.22).translate(0, 0, -0.38),
  ]);
}

export function raiderGeometry(): BufferGeometry {
  return merge([
    nose(new ConeGeometry(0.32, 0.8, 3)).scale(1, 0.45, 1),
    new BoxGeometry(0.05, 0.26, 0.3).translate(0.2, 0.1, -0.22),
    new BoxGeometry(0.05, 0.26, 0.3).translate(-0.2, 0.1, -0.22),
  ]);
}

export function linerGeometry(): BufferGeometry {
  return merge([
    new BoxGeometry(0.28, 0.22, 0.86),
    new BoxGeometry(0.14, 0.14, 0.52).translate(0.23, -0.02, -0.06),
    new BoxGeometry(0.14, 0.14, 0.52).translate(-0.23, -0.02, -0.06),
    nose(new ConeGeometry(0.14, 0.2, 4)).translate(0, 0, 0.53),
  ]);
}

/** The constructor: a squat hull with a crane arm, the station it will become. */
export function constructorGeometry(): BufferGeometry {
  return merge([
    new BoxGeometry(0.4, 0.26, 0.5),
    new BoxGeometry(0.05, 0.05, 0.5).rotateX(-0.6).translate(0.1, 0.27, 0.12),
    new BoxGeometry(0.14, 0.1, 0.14).translate(0.1, 0.42, 0.3),
    nose(new CylinderGeometry(0.1, 0.14, 0.14, 6)).translate(0, 0, -0.32),
  ]);
}

/** The lander (#135): a broad wedge with drop pods slung under its wings. */
export function landerGeometry(): BufferGeometry {
  return merge([
    nose(new ConeGeometry(0.3, 0.7, 4)).scale(1, 0.55, 1),
    new BoxGeometry(0.66, 0.05, 0.26).translate(0, 0, -0.16),
    new CylinderGeometry(0.07, 0.05, 0.16, 5).translate(0.26, -0.1, -0.16),
    new CylinderGeometry(0.07, 0.05, 0.16, 5).translate(-0.26, -0.1, -0.16),
    new CylinderGeometry(0.07, 0.05, 0.16, 5).translate(0, -0.12, -0.06),
  ]);
}
